import { useSelector } from 'react-redux';
import { InventoryTypeId } from '~/constants/inventoryType';
import Store from '~/constants/store';
import { RecommendedVehicle } from '~/contracts/vdp';
import { getVehicleDetails } from '~/selectors';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import DeliveryInfo from './DeliveryInfo';
import Thumbnail from './Thumbnail';
import useAnalyticsAttributes from './useAnalyticsAttributes';
import useAnchorAttributes from './useAnchorAttributes';
import useOnClick from './useOnClick';
import {
  DetailsGrid,
  DetailsWrapper,
  LowerSectionWrapper,
  Price,
  TrimAndMileage,
  Wrapper,
  YearMakeModel
} from './VehicleTile.styles';

const useLabel = (vehicle: RecommendedVehicle) => {
  const is3PR = vehicle.storeKey === Store.Manheim;
  const isPartner =
    vehicle.vehicleInventoryType === InventoryTypeId.DealerMarketplace ||
    vehicle.storeKey === Store.DMP2Core;
  const isLightAccident = vehicle.storeKey === Store.LightAccident;

  switch (true) {
    case isPartner:
      return 'Partner';
    case is3PR || isLightAccident:
      return null;
    default:
      return 'Carvana Certified';
  }
};

type OwnProps = {
  vehicle: RecommendedVehicle;
};

export default function VehicleTile({ vehicle }: OwnProps) {
  const {
    price: { total },
    vehicleId
  } = vehicle;

  const analyticsAttributes = useAnalyticsAttributes(vehicleId);
  const anchorAttributes = useAnchorAttributes(vehicleId);
  const label = useLabel(vehicle);
  const { price: priceOfVDP = 0 } = useSelector(getVehicleDetails);
  const hasLowerPrice = total < priceOfVDP;
  const handleClick = useOnClick();

  return (
    <Wrapper
      data-qa="vehicle-tile"
      data-inventory-type={vehicle.vehicleInventoryType}
    >
      <Thumbnail hasLowerPrice={hasLowerPrice} vehicle={vehicle} />
      <a
        data-analytics-id={`Vehicles You Might Like Tile${
          hasLowerPrice ? ' - Lower Price' : ''
        }`}
        data-analytics-attributes={analyticsAttributes}
        onClick={handleClick}
        {...anchorAttributes}
      >
        <DetailsWrapper>
          <DetailsGrid>
            <p className="text-grey-3 tl-label-xs">{label || ''}</p>
            <YearMakeModel>
              {vehicle.year} {vehicle.make} {vehicle.model}
            </YearMakeModel>
            <TrimAndMileage>
              {vehicle.trim} <span className="px-4">•</span>{' '}
              {vehicle.mileage.toLocaleString()} miles
            </TrimAndMileage>
          </DetailsGrid>
          <div className="mb-12 flex items-end">
            <Price>${formatAndDropDecimal(total)}</Price>
          </div>
          <div>
            <div className="border-b border-grey-2" />
            <LowerSectionWrapper>
              <DeliveryInfo vehicle={vehicle} />
            </LowerSectionWrapper>
          </div>
        </DetailsWrapper>
      </a>
    </Wrapper>
  );
}
